import { useRef, useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitEnquiry } from "@/lib/api";
import { trackEvent, getTracking } from "@/lib/analytics";

export const inputCls =
  "w-full rounded-[10px] border border-line bg-white px-4 py-3 text-sm text-ink placeholder:text-[#9AAABB] outline-none transition-[border-color,box-shadow] duration-200 focus:border-brand-cyan focus:ring-2 focus:ring-brand-cyan/20";

export const Field = ({ label, required, error, children }) => (
  <label className="block">
    <span className="mb-1.5 block font-display text-xs font-bold uppercase tracking-[0.12em] text-navy">
      {label}
      {required && <span className="ml-0.5 text-brand-blue">*</span>}
    </span>
    {children}
    {error && (
      <span className="mt-1 block text-xs font-medium text-red-600" role="alert">
        {error}
      </span>
    )}
  </label>
);

export const RadioPills = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" role="radiogroup" data-testid={testid}>
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        role="radio"
        aria-checked={value === opt}
        data-testid={`${testid}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onClick={() => onChange(opt)}
        className={`rounded-[10px] border px-4 py-2.5 text-xs font-semibold transition-[background-color,border-color,color] duration-200 ${
          value === opt
            ? "border-brand-blue bg-mist text-navy"
            : "border-line bg-white text-mutedblue hover:border-brand-cyan hover:text-navy"
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

export const Honeypot = ({ value, onChange }) => (
  <input
    type="text"
    name="website"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="hidden"
    tabIndex={-1}
    autoComplete="off"
    aria-hidden="true"
  />
);

export const SubmitButton = ({ status, children, testid }) => (
  <button
    type="submit"
    data-testid={testid}
    disabled={status === "submitting"}
    className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-gradient-brand px-7 py-4 font-display text-sm font-bold tracking-wide text-white shadow-[0_8px_24px_rgba(0,140,196,0.25)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(0,140,196,0.38)] disabled:opacity-60 disabled:hover:translate-y-0 md:w-auto"
  >
    {status === "submitting" && <Loader2 className="h-4 w-4 animate-spin" />}
    {status === "submitting" ? "Submitting…" : children}
  </button>
);

export const SuccessPanel = ({ title, message, onReset, testid }) => (
  <div
    className="rounded-2xl border border-brand-cyan/40 bg-ice p-8 text-center"
    data-testid={testid}
  >
    <CheckCircle2 className="mx-auto h-10 w-10 text-brand-blue" />
    <h3 className="mt-4 font-display text-xl font-bold text-navy">{title}</h3>
    <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-mutedblue">{message}</p>
    <button
      type="button"
      data-testid={`${testid}-reset`}
      onClick={onReset}
      className="mt-5 font-display text-xs font-bold uppercase tracking-[0.15em] text-brand-blue transition-colors duration-200 hover:text-navy"
    >
      Submit another enquiry
    </button>
  </div>
);

const SUBMIT_EVENTS = {
  quote: "quote_form_submit",
  contact: "quote_form_submit",
  veu: "veu_form_submit",
  warranty: "warranty_form_submit",
};

export function useEnquiry(type, requiredFields, page) {
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const startedRef = useRef(false);
  const renderTs = useRef(Date.now());
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const formProps = {
    onFocusCapture: () => {
      if (!startedRef.current) {
        startedRef.current = true;
        if (type === "quote" || type === "contact") {
          trackEvent("quote_form_start", { source_page: page });
        }
      }
    },
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    requiredFields.forEach((k) => {
      if (!String(data[k] || "").trim()) errs[k] = "This field is required";
    });
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(data.email).trim()))
      errs.email = "Enter a valid email address";
    if (data.phone && !/^[+\d][\d\s()\-]{7,}$/.test(String(data.phone).trim()))
      errs.phone = "Enter a valid phone number";
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setStatus("submitting");
    try {
      const payload = { ...data, ...getTracking(), _ts: String(renderTs.current) };
      await submitEnquiry({ type, data: payload, source_page: page, website: data.website || "" });
      setStatus("success");
      trackEvent(SUBMIT_EVENTS[type] || "quote_form_submit", { form_type: type, source_page: page });
      toast.success("Enquiry submitted — our team will be in touch shortly.");
    } catch (err) {
      setStatus("error");
      toast.error("Something went wrong. Please try again or call us directly.");
    }
  };

  return {
    data,
    set,
    errors,
    status,
    submit,
    formProps,
    reset: () => {
      setData({});
      setErrors({});
      setStatus("idle");
      startedRef.current = false;
      renderTs.current = Date.now();
    },
  };
}
